import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowLeft, Globe, Sparkles } from 'lucide-react';
import api from '../lib/api';

const VISA_HERO = "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?q=80&w=2074";

const destinationImages = {
  schengen: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?q=80&w=800",
  canada: "https://images.unsplash.com/photo-1517935706615-2717063c2225?q=80&w=800",
  usa: "https://images.unsplash.com/photo-1485738422979-f5c462d49f74?q=80&w=800",
  dubai: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?q=80&w=800",
  south_africa: "https://images.unsplash.com/photo-1580060839134-75a5edca2e99?q=80&w=800",
  china: "https://images.unsplash.com/photo-1508804185872-d7badad00f7d?q=80&w=800",
  india: "https://images.unsplash.com/photo-1524492412937-b28074a5d7da?q=80&w=800"
};

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } }
};

export const VisaPage = () => {
  const navigate = useNavigate();
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDestinations();
  }, []);

  const fetchDestinations = async () => {
    try {
      const response = await api.get('/visa-destinations');
      setDestinations(response.data);
    } catch (error) {
      console.error('Error fetching destinations:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#020617] overflow-x-hidden">
      {/* Hero */}
      <section className="relative min-h-[25vh] flex items-center" data-testid="visa-hero">
        <div className="absolute inset-0">
          <img src={VISA_HERO} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#020617] via-[#020617]/80 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-transparent to-[#020617]/50" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-6 w-full">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-[#94A3B8] hover:text-[#D4AF37] transition-colors mb-3"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </button>
          
          <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
            <motion.div variants={fadeInUp} className="flex items-center gap-2 mb-2">
              <div className="h-px w-8 bg-[#D4AF37]" />
              <span className="text-[#D4AF37] text-xs md:text-sm font-medium tracking-wider uppercase">
                Assistance Visa
              </span>
            </motion.div>
            
            <motion.h1 variants={fadeInUp} className="text-2xl md:text-5xl font-bold text-white leading-tight mb-2">
              Simplifiez vos <span className="text-[#D4AF37] italic">Démarches</span>
            </motion.h1>
            
            <motion.p variants={fadeInUp} className="text-sm md:text-lg text-[#94A3B8] max-w-2xl">
              Taux de réussite de 98% depuis 2011.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Destinations Grid - Airbnb Style */}
      <section className="py-6 md:py-12 relative" data-testid="visa-destinations">
        <div className="absolute inset-0 gradient-radial opacity-30" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 relative">
          <motion.div 
            className="text-center mb-6 md:mb-10"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-10 bg-gradient-to-r from-transparent to-[#D4AF37]" />
              <Sparkles className="w-4 h-4 text-[#D4AF37]" />
              <div className="h-px w-10 bg-gradient-to-l from-transparent to-[#D4AF37]" />
            </div>
            <h2 className="text-xl md:text-4xl font-bold text-white mb-2">
              Choisissez votre <span className="text-[#D4AF37] italic">destination</span>
            </h2>
            <p className="text-[#94A3B8] text-xs md:text-lg max-w-2xl mx-auto">
              Découvrez les types de visa disponibles et leurs tarifs
            </p>
          </motion.div>

          {loading ? (
            <div className="grid grid-cols-2 gap-3 md:gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[4/3] rounded-xl bg-white/5" />
                  <div className="mt-2 h-4 bg-white/5 rounded w-3/4" />
                  <div className="mt-1 h-3 bg-white/5 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : (
            <motion.div 
              className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-6"
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
            >
              {destinations.map((dest) => (
                <motion.div key={dest.id} variants={fadeInUp}>
                  <Link
                    to={`/visa/${dest.id}`}
                    className="block group"
                    data-testid={`visa-dest-${dest.id}`}
                  >
                    {/* Airbnb Card: Image on top, info below */}
                    <div className="aspect-[4/3] rounded-xl overflow-hidden relative mb-2">
                      <img 
                        src={destinationImages[dest.id] || VISA_HERO}
                        alt={dest.country}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors" />
                    </div>
                    <h3 className="text-sm md:text-base font-semibold text-white group-hover:text-[#D4AF37] transition-colors">
                      {dest.country}
                    </h3>
                    <p className="text-[10px] md:text-xs text-[#64748B]">
                      {dest.visa_types?.length || 0} types de visa
                    </p>
                  </Link>
                </motion.div>
              ))}

              {/* Other Destinations */}
              <motion.div variants={fadeInUp}>
                <Link to="/visa/autres" className="block group" data-testid="visa-dest-autres">
                  <div className="aspect-[4/3] rounded-xl overflow-hidden relative mb-2 bg-gradient-to-br from-[#1E293B] to-[#0F172A] border border-white/10 flex items-center justify-center">
                    <Globe className="w-10 h-10 md:w-16 md:h-16 text-[#D4AF37]/50 group-hover:text-[#D4AF37] transition-colors" />
                  </div>
                  <h3 className="text-sm md:text-base font-semibold text-white group-hover:text-[#D4AF37] transition-colors">
                    Autres Destinations
                  </h3>
                  <p className="text-[10px] md:text-xs text-[#64748B]">
                    Demande personnalisée
                  </p>
                </Link>
              </motion.div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Process */}
      <section className="py-6 md:py-12 relative" data-testid="visa-process">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <motion.div 
            className="text-center mb-6 md:mb-10"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-xl md:text-4xl font-bold text-white mb-2">
              Notre <span className="text-[#D4AF37] italic">Processus</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
            {[
              { step: 1, title: 'Consultation', desc: 'Évaluation de votre dossier' },
              { step: 2, title: 'Constitution', desc: 'Préparation des documents' },
              { step: 3, title: 'Soumission', desc: 'Dépôt auprès des autorités' },
              { step: 4, title: 'Suivi', desc: "Jusqu'à l'obtention" }
            ].map((item, index) => (
              <motion.div 
                key={index}
                className="text-center"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
              >
                <div className="w-12 h-12 md:w-20 md:h-20 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-lg md:text-2xl font-bold flex items-center justify-center mx-auto mb-3 md:mb-6">
                  {item.step}
                </div>
                <h3 className="text-xs md:text-xl font-semibold text-white mb-1">{item.title}</h3>
                <p className="text-[10px] md:text-sm text-[#94A3B8]">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 md:py-24 relative" data-testid="visa-cta">
        <div className="absolute inset-0 bg-gradient-to-r from-[#D4AF37]/5 via-transparent to-[#D4AF37]/5" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-12 text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h2 className="text-xl md:text-4xl font-bold text-white mb-3">
              Besoin d'<span className="text-[#D4AF37] italic">Assistance</span> ?
            </h2>
            <p className="text-[#94A3B8] text-xs md:text-lg mb-6 md:mb-10">
              Notre équipe d'experts est disponible pour vous
            </p>
            <div className="flex justify-center gap-3">
              <a href="https://wa.me/243975608422" target="_blank" rel="noopener noreferrer" data-testid="visa-whatsapp">
                <button className="btn-gold px-5 py-2.5 md:px-10 md:py-4 text-xs md:text-base">
                  WhatsApp
                </button>
              </a>
              <a href="tel:+243975608422">
                <button className="btn-glass px-5 py-2.5 md:px-10 md:py-4 text-xs md:text-base text-white">
                  Appeler
                </button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default VisaPage;
